import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileSpreadsheet, X } from 'lucide-react';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { useDateFilter } from '../contexts/DateFilterContext';
import { filterLeafAccounts } from '../lib/coa-hierarchy';
import DropdownSelect from './DropdownSelect';
import { exportStatementExcel, safeFilePart, type StatementExportRow } from '../lib/statement-export';

// Statement drill-down modal (workflow-home-demo.html drillModal, Task 12
// scope ruling) — the voucher level only. The embedded Bookkeeping keeps its
// own drills authoritative (account rows → account transactions; the P&L
// formula banner → COA breakdowns); this modal adds the missing voucher view:
// pick one leaf account → list every voucher that hits it in the selected
// fiscal year (GET /bookkeeping/entries, same endpoint + start/end + account
// filters the GJE tab uses). Entries whose journal_entries.reference_type is
// 'invoice' carry resolved_links.invoice (invoice_number/direction), rendered
// as a blue chip that jumps to /ar (sales, outgoing) or /ap (purchase,
// incoming).

interface DrillEntry {
  id: string;
  entry_number: string;
  entry_date: string;
  description: string | null;
  total_debit: number;
  total_credit: number;
  reference_type?: string | null;
  reference_id?: string | null;
  resolved_links?: {
    invoice?: { invoice_number: string; direction: string } | null;
  } | null;
}

const fmtAmt = (n: number) => Math.abs(n || 0).toLocaleString('en-HK', { minimumFractionDigits: 2 });

export default function StatementDrillModal({ onClose, tenantName }: {
  onClose: () => void;
  /** Optional tenant label for the download filename. */
  tenantName?: string | null;
}) {
  useTranslation(); // subscribe to language changes so tr() updates
  const navigate = useNavigate();
  const { selectedFY, startDate, endDate } = useDateFilter();
  const [accountCode, setAccountCode] = useState('');

  // Leaf (postable) accounts of the tenant — the picker list.
  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api('/bookkeeping/accounts'),
  });
  const leafAccounts = useMemo(() => filterLeafAccounts(accounts?.data || []), [accounts]);
  const picked = leafAccounts.find((a: any) => String(a.account_code) === accountCode);

  // Vouchers hitting the picked account within the FY (the FY selector drives
  // start/end via DateFilterContext — same scoping as the embedded statements).
  const { data: entries, isLoading } = useQuery({
    queryKey: ['entries', startDate, endDate, 'drill', accountCode],
    queryFn: () => api(`/bookkeeping/entries?start_date=${startDate}&end_date=${endDate}&limit=2000&account=${encodeURIComponent(accountCode)}`),
    enabled: !!accountCode,
  });

  const rows: DrillEntry[] = entries?.data || [];
  const total = rows.reduce((s, e) => s + (e.total_debit || 0), 0);

  const invoiceOf = (e: DrillEntry): { label: string; to: string } | null => {
    if (e.reference_type !== 'invoice') return null;
    const inv = e.resolved_links?.invoice;
    return {
      label: inv?.invoice_number || e.reference_id || '—',
      to: inv?.direction === 'outgoing' ? '/ar' : '/ap',
    };
  };

  const exportExcel = () => {
    if (!accountCode || rows.length === 0) return;
    const exportRows: StatementExportRow[] = rows.map(e => {
      const inv = invoiceOf(e);
      return {
        label: e.entry_number,
        date: e.entry_date,
        description: e.description ?? '',
        invoice: inv?.label ?? '',
        amount: e.total_debit,
      };
    });
    exportStatementExcel('vouchers', exportRows, {
      title: tr(
        `Vouchers — ${accountCode} ${picked?.account_name || ''} (${startDate} to ${endDate})`,
        `憑證明細 — ${accountCode} ${picked?.account_name || ''}（${startDate} 至 ${endDate}）`,
        `凭证明细 — ${accountCode} ${picked?.account_name || ''}（${startDate} 至 ${endDate}）`,
      ),
      filename: `${safeFilePart(tenantName) ? safeFilePart(tenantName) + '_' : ''}FY${selectedFY}_vouchers_${accountCode}`,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-[820px] mx-4 max-h-[85vh] overflow-y-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — back arrow (clears the picked account) + title + actions */}
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            {accountCode && (
              <button
                onClick={() => setAccountCode('')}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground shrink-0"
                aria-label={tr('Back', '返回', '返回')}
                title={tr('Back to account picker', '返回科目選擇', '返回科目选择')}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <h3 className="font-semibold truncate">
              {accountCode && picked
                ? `${accountCode} – ${picked.account_name}`
                : tr('Voucher breakdown', '憑證明細 Vouchers', '凭证明细 Vouchers')}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={exportExcel}
              disabled={!accountCode || rows.length === 0}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={tr('Export Excel (breakdown)', '導出 Excel（明細）', '导出 Excel（明细）')}
              title={tr('Export Excel (breakdown)', '導出 Excel（明細）', '导出 Excel（明细）')}
            >
              <FileSpreadsheet className="h-4 w-4 text-green-600" />
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground" aria-label={tr('Close', '關閉', '关闭')}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* L1 — account picker; L2 — the picked account's vouchers */}
        {!accountCode ? (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-2">
              {tr(
                'Pick an account to see its vouchers for the period.',
                '選擇科目查看本期憑證。',
                '选择科目查看本期凭证。',
              )}
            </p>
            <DropdownSelect
              value=""
              onChange={setAccountCode}
              className="max-w-md"
              placeholder={tr('Account — pick a leaf account', '科目——選擇明細科目', '科目——选择明细科目')}
              options={leafAccounts.map((a: any) => ({ value: String(a.account_code), label: `${a.account_code} – ${a.account_name}` }))}
            />
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              {tr('Vouchers hitting this account', '此科目的憑證', '此科目的凭证')}
              {' · '}{startDate} → {endDate}
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 text-muted-foreground">
                  <th className="text-left px-3 py-2 font-medium">{tr('Voucher', '憑證', '凭证')}</th>
                  <th className="text-left px-3 py-2 font-medium">{tr('Date', '日期', '日期')}</th>
                  <th className="text-left px-3 py-2 font-medium">{tr('Description', '摘要', '摘要')}</th>
                  <th className="text-left px-3 py-2 font-medium">{tr('Invoice', '發票', '发票')}</th>
                  <th className="text-right px-3 py-2 font-medium">{tr('Amount', '金額', '金额')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">{tr('Loading…', '載入中…', '载入中…')}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    {tr('No vouchers hit this account in the period.', '本期沒有憑證涉及此科目。', '本期没有凭证涉及此科目。')}
                  </td></tr>
                ) : rows.map(e => {
                  const inv = invoiceOf(e);
                  return (
                    <tr key={e.id} className="border-b border-muted/30 hover:bg-muted/20">
                      <td className="px-3 py-2 font-medium font-mono whitespace-nowrap">{e.entry_number}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{e.entry_date}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate" title={e.description || ''}>{e.description || '—'}</td>
                      <td className="px-3 py-2">
                        {inv ? (
                          <button
                            onClick={() => { onClose(); navigate(inv.to); }}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200"
                            title={tr('Open invoice list', '前往發票清單', '前往发票清单')}
                          >
                            {inv.label}
                          </button>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtAmt(e.total_debit)}</td>
                    </tr>
                  );
                })}
                {rows.length > 0 && (
                  <tr className="bg-muted/30 font-semibold">
                    <td colSpan={4} className="px-3 py-2">{tr('Total', '合計', '合计')}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtAmt(total)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
